import React, { useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface AuthCallbackProps {
  onSuccess: (token: string) => void;
  onError: (error: string) => void;
}

/**
 * Componente que maneja el callback de Google OAuth
 * 
 * Flujo:
 * 1. Usuario se autentica en Google
 * 2. Google redirige a /auth/callback?token=xxx
 * 3. Este componente extrae el token
 * 4. Decodifica el JWT para obtener datos del usuario
 * 5. Guarda en localStorage
 * 6. Notifica a App.tsx para actualizar estado
 */
const AuthCallback: React.FC<AuthCallbackProps> = ({ onSuccess, onError }) => {
  useEffect(() => {
    // Extraer token de query params
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const error = urlParams.get('error');

    if (error) {
      console.error('❌ [Auth Callback] Error from backend:', error);
      onError(getErrorMessage(error));
      return;
    }

    if (!token) {
      console.error('❌ [Auth Callback] No token received');
      onError('No se recibió token de autenticación');
      return;
    }

    try {
      // Decodificar JWT para obtener datos del usuario
      const payload = JSON.parse(atob(token.split('.')[1]));

      console.log('✅ [Auth Callback] Token recibido');
      console.log('   User:', payload.email);
      console.log('   empresaId:', payload.empresaId || 'null (requiere onboarding)');

      // Guardar token
      localStorage.setItem('token', token);

      // Preparar datos del usuario para el estado
      const userData = {
        id: payload.sub,
        email: payload.email,
        name: payload.name,
        role: payload.role,
        type: payload.type,
        userType: payload.userType,
        empresaId: payload.empresaId,
      };

      // Guardar usuario en localStorage
      localStorage.setItem('currentUser', JSON.stringify(userData));

      // Notificar éxito
      onSuccess(token);

    } catch (error) {
      console.error('❌ [Auth Callback] Error procesando token:', error);
      onError('Error procesando token de autenticación');
    }
  }, [onSuccess, onError]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-sky-500 animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-white mb-2">
          Completando autenticación...
        </h2>
        <p className="text-slate-400">
          Por favor espera un momento
        </p>
      </div>
    </div>
  );
};

function getErrorMessage(error: string): string {
  const errorMessages: Record<string, string> = {
    'auth_failed': 'Falló la autenticación con Google',
    'server_error': 'Error del servidor',
    'callback_failed': 'Error en el callback de Google',
    'email_not_verified': 'El email no está verificado en Google',
  };

  return errorMessages[error] || 'Error desconocido en la autenticación';
}

export default AuthCallback;
